import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { API } from "@/lib/admin-constants";
import {
  Plus, Trash2, Loader2, Calendar, MapPin, Clock, Pencil, Eye, EyeOff, Tag,
} from "lucide-react";

const EVENT_TYPES = [
  { id: "tryout", label: "Tryout" },
  { id: "game", label: "Game" },
  { id: "tournament", label: "Tournament" },
  { id: "training", label: "Training" },
  { id: "meeting", label: "Meeting" },
  { id: "general", label: "General" },
];

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", event_type: "general", date: "", end_date: "",
    time: "", end_time: "", location: "", is_public: true, age_groups: "", registration_url: "",
  });

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(res.data);
    } catch { console.error("Failed to fetch events"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const resetForm = () => {
    setForm({ title: "", description: "", event_type: "general", date: "", end_date: "", time: "", end_time: "", location: "", is_public: true, age_groups: "", registration_url: "" });
    setEditing(null);
  };

  const openNew = () => { resetForm(); setSheetOpen(true); };
  const openEdit = (evt) => {
    setEditing(evt);
    setForm({
      title: evt.title || "",
      description: evt.description || "",
      event_type: evt.event_type || "general",
      date: evt.date || "",
      end_date: evt.end_date || "",
      time: evt.time || "",
      end_time: evt.end_time || "",
      location: evt.location || "",
      is_public: evt.is_public !== false,
      age_groups: (evt.age_groups || []).join(", "),
      registration_url: evt.registration_url || "",
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Title and date required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      age_groups: form.age_groups ? form.age_groups.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (editing) {
        await axios.put(`${API}/events/${editing.event_id}`, payload, { withCredentials: true });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, payload, { withCredentials: true });
        toast.success("Event created");
      }
      setSheetOpen(false);
      resetForm();
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success("Event deleted");
      fetchEvents();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cfc-primary" size={32} /></div>;

  const upcoming = events.filter((e) => e.date >= new Date().toISOString().slice(0, 10));
  const past = events.filter((e) => e.date < new Date().toISOString().slice(0, 10));

  return (
    <div data-testid="events-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Events Calendar</h1>
          <p className="text-sm text-cfc-text-muted mt-1">{events.length} events &middot; {upcoming.length} upcoming</p>
        </div>
        <Button className="bg-cfc-primary text-white" onClick={openNew} data-testid="new-event-btn">
          <Plus size={16} className="mr-1.5" /> Add Event
        </Button>
      </div>

      {events.length === 0 ? (
        <Card className="border-cfc-border">
          <CardContent className="flex flex-col items-center py-16 text-cfc-text-muted">
            <Calendar size={48} className="mb-4 opacity-40" />
            <p className="text-lg font-medium mb-1">No events yet</p>
            <p className="text-sm mb-4">Add tryouts, games, tournaments to your public calendar</p>
            <Button onClick={openNew} variant="outline"><Plus size={16} className="mr-1.5" /> Create Event</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <h2 className="text-sm font-bold uppercase text-cfc-text-muted tracking-wider mb-3">Upcoming</h2>
              <div className="space-y-2">
                {upcoming.map((evt) => (
                  <EventRow key={evt.event_id} event={evt} onEdit={() => openEdit(evt)} onDelete={() => handleDelete(evt.event_id)} />
                ))}
              </div>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <h2 className="text-sm font-bold uppercase text-cfc-text-muted tracking-wider mb-3">Past</h2>
              <div className="space-y-2 opacity-60">
                {past.map((evt) => (
                  <EventRow key={evt.event_id} event={evt} onEdit={() => openEdit(evt)} onDelete={() => handleDelete(evt.event_id)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Form Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm(); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading text-cfc-primary">{editing ? "Edit Event" : "New Event"}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Title *</label>
              <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Fall Evaluation Session" className="mt-1" data-testid="event-title-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Type</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {EVENT_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setForm((p) => ({ ...p, event_type: t.id }))}
                    className={`px-2.5 py-1 text-[11px] font-bold rounded-sm border transition-colors ${form.event_type === t.id ? "bg-cfc-primary text-white border-cfc-primary" : "border-cfc-border text-cfc-text-muted"}`}
                    data-testid={`event-type-${t.id}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">Date *</label>
                <Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="mt-1" data-testid="event-date-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">End Date</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} className="mt-1" data-testid="event-end-date-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">Start Time</label>
                <Input value={form.time} onChange={(e) => setForm((p) => ({ ...p, time: e.target.value }))} placeholder="e.g. 6:30 PM" className="mt-1" data-testid="event-time-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">End Time</label>
                <Input value={form.end_time} onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))} placeholder="e.g. 8:00 PM" className="mt-1" data-testid="event-end-time-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Location</label>
              <Input value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} placeholder="e.g. Pen Park Field 3" className="mt-1" data-testid="event-location-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Age Groups (comma separated)</label>
              <Input value={form.age_groups} onChange={(e) => setForm((p) => ({ ...p, age_groups: e.target.value }))} placeholder="e.g. U10, U12, U14" className="mt-1" data-testid="event-age-groups-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Registration URL</label>
              <Input value={form.registration_url} onChange={(e) => setForm((p) => ({ ...p, registration_url: e.target.value }))} placeholder="https://..." className="mt-1" data-testid="event-reg-url-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Details about the event..." className="mt-1" data-testid="event-description-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((p) => ({ ...p, is_public: e.target.checked }))} className="accent-cfc-primary" data-testid="event-public-checkbox" />
              <label className="text-sm text-cfc-primary font-medium">Show on public events page</label>
            </div>
            <Separator />
            <Button className="w-full bg-cfc-primary text-white" onClick={handleSave} disabled={saving} data-testid="save-event-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              {editing ? "Update Event" : "Create Event"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const EventRow = ({ event, onEdit, onDelete }) => (
  <div className="flex items-center gap-4 bg-white border border-cfc-border rounded-sm p-3" data-testid={`event-row-${event.event_id}`}>
    <div className="w-11 h-11 rounded-sm bg-cfc-primary flex flex-col items-center justify-center flex-shrink-0">
      <span className="text-[9px] text-[#C8E64E] font-bold uppercase">
        {new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "short" })}
      </span>
      <span className="text-base text-white font-bold leading-none">
        {new Date(event.date + "T00:00:00").getDate()}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <h3 className="font-medium text-sm text-cfc-primary truncate">{event.title}</h3>
        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-cfc-cream-alt text-cfc-text-muted border border-cfc-border">{event.event_type}</span>
        {!event.is_public && <EyeOff size={12} className="text-cfc-text-muted" title="Hidden" />}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-cfc-text-muted mt-0.5">
        {event.time && <span className="flex items-center gap-0.5"><Clock size={10} /> {event.time}</span>}
        {event.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={10} /> {event.location}</span>}
        {event.age_groups?.length > 0 && <span className="flex items-center gap-0.5"><Tag size={10} /> {event.age_groups.join(", ")}</span>}
      </div>
    </div>
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`edit-event-${event.event_id}`}><Pencil size={14} /></Button>
      <Button variant="ghost" size="sm" className="text-red-500" onClick={onDelete} data-testid={`delete-event-${event.event_id}`}><Trash2 size={14} /></Button>
    </div>
  </div>
);

export default EventsManager;
